import type React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { Autocomplete, TextField, useTheme } from '@mui/material';
import { t } from 'i18next';
import { useGetSubscriptionList } from '@queryHooks/useSubscription';
import type { SubscriptionListModel } from '@models/SubscriptionListModel';

interface Props {
  value?: string;
  onChange: (subscription: SubscriptionListModel) => void;
  isEditable: boolean;
}

const SubscriptionAutocomplete = ({ onChange, value, isEditable }: Props) => {
  const theme = useTheme();
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionListModel | null>(null);

  const { data: subscriptions } = useGetSubscriptionList(undefined);

  useEffect(() => {
    if (subscriptions?.content && value) {
      const initSubscription = subscriptions.content.find(sb => sb.id === value);
      if (initSubscription) {
        setSelectedSubscription(initSubscription);
      }
    } else if (!value) {
      setSelectedSubscription(null);
    }
  }, [subscriptions?.content, value]);

  const handleChangeAutocomplete = (
    _event: React.SyntheticEvent<Element, Event>,
    value: SubscriptionListModel | null,
  ) => {
    setSelectedSubscription(value);
    if (value) onChange(value);
  };

  // 커스텀 필터링 함수
  const filterOptions = useCallback((options: SubscriptionListModel[], state: { inputValue: string }) => {
    const inputValue = state.inputValue.toLowerCase().trim();

    if (!inputValue) {
      return options;
    }

    const filtered = options.filter(option => {
      const name = (option.name || '').toLowerCase();
      const matches = name.includes(inputValue);
      return matches;
    });

    return filtered;
  }, []);

  return (
    <Autocomplete
      disabled={!isEditable}
      className="autocomplete"
      value={selectedSubscription}
      size="small"
      options={subscriptions?.content || []}
      filterOptions={filterOptions}
      getOptionKey={option => option.id}
      renderInput={params => (
        <TextField
          {...params}
          size="medium"
          placeholder={t('Subscription_Auto_Complete_01')}
          sx={{
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderRadius: '2px 2px 0 0',
            },
            '& .MuiInputBase-input.Mui-disabled': {
              color: `${theme.colors['text-gray-light']}`,
            },
          }}
        />
      )}
      fullWidth
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) return false;
        return option.id === value.id;
      }}
      getOptionLabel={subscription => subscription?.name || ''}
      onChange={handleChangeAutocomplete}
      ListboxProps={{
        style: {
          maxHeight: '328px',
        },
      }}
      componentsProps={{
        paper: {
          sx: {
            width: '100%',
            marginTop: 0,
            borderRadius: '0 0 2px 2px',
            boxShadow: '0',
            border: `1px solid ${theme.colors['border-gray-light']}`,
            borderWidth: '0 1px 1px 1px',
            overflow: 'hidden',
            '& .MuiList-root': {
              border: 'none',
              padding: '4px 0',
              '& .MuiMenuItem-root': {
                margin: '0 5px',
              },
            },
          },
        },
      }}
      sx={{
        '&.MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input': {
          padding: '7.5px 4px 7.5px 12px',
        },
      }}
    />
  );
};

export default SubscriptionAutocomplete;
